import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/components/auth/AuthContext';

const EmailVerification: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();

  useEffect(() => {
    if (!token) {
      setIsVerifying(false);
      setErrorMessage('Invalid verification link. Token is missing.');
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      setIsVerifying(true);
      console.log('[VERIFY-DEBUG] Starting email verification with token:', token);
      
      // Fetch the token record from the database
      const { data: tokenData, error: tokenError } = await supabase
        .from('email_verification_tokens')
        .select('*')
        .eq('token', token)
        .single();
      
      console.log('[VERIFY-DEBUG] Token lookup result:', { tokenData, tokenError });
      
      if (tokenError || !tokenData) {
        console.error('[VERIFY-DEBUG] Token error or not found:', tokenError);
        throw new Error('Invalid verification token');
      }
      
      // Check if token has expired
      const now = new Date();
      const expiryDate = new Date(tokenData.expires_at);
      const isExpired = expiryDate < now;
      
      console.log('[VERIFY-DEBUG] Token expiry check:', { 
        expiryDate: tokenData.expires_at,
        currentDate: now.toISOString(),
        isExpired
      });
      
      if (isExpired) {
        console.error('[VERIFY-DEBUG] Token has expired');
        throw new Error('Verification link has expired. Please request a new one.');
      }
      
      // Check if token is already verified
      console.log('[VERIFY-DEBUG] Token verification status:', { isVerified: tokenData.is_verified });
      if (tokenData.is_verified) {
        console.log('[VERIFY-DEBUG] Token already verified, skipping update');
        setIsSuccess(true);
        setIsVerifying(false);
        return;
      }
      
      // Update user's profile with the company email and reset the verification flag
      console.log('[VERIFY-DEBUG] Updating profile with company email:', tokenData.company_email);
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          company_email: tokenData.company_email,
          needs_manual_verification: false,
          verified_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', tokenData.user_id);
      
      if (profileUpdateError) {
        console.error('[VERIFY-DEBUG] Profile update error:', profileUpdateError);
        throw new Error('Failed to update your profile with the verified email');
      }
      
      // Mark the token as verified
      console.log('[VERIFY-DEBUG] Marking token as verified');
      const { error: tokenUpdateError } = await supabase
        .from('email_verification_tokens')
        .update({
          is_verified: true,
          verified_at: new Date().toISOString()
        })
        .eq('id', tokenData.id);
        
      if (tokenUpdateError) {
        console.error('[VERIFY-DEBUG] Token update error:', tokenUpdateError);
        // Don't throw here, as the profile is already updated
      }
      
      // If the current user is the one being verified, refresh their session
      console.log('[VERIFY-DEBUG] Current user check:', { 
        currentUser: user?.id, 
        tokenUser: tokenData.user_id,
        isSameUser: user?.id === tokenData.user_id 
      });
      
      if (user?.id === tokenData.user_id) {
        console.log('[VERIFY-DEBUG] Refreshing session for current user');
        await refreshSession();
      }
      
      console.log('[VERIFY-DEBUG] Verification completed successfully');
      setIsSuccess(true);
    } catch (error: any) {
      console.error('[VERIFY-DEBUG] Error verifying email:', error);
      setIsSuccess(false);
      setErrorMessage(error.message || 'An error occurred during verification');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleSignIn = () => {
    navigate('/sign-in');
  };

  return (
    <div className="min-h-screen bg-[#FBFAFF] flex flex-col">
      <header className="bg-white border-b border-[#9b87f5]/10 py-4">
        <div className="container mx-auto px-4 flex justify-center">
          <img src="/images/logo.svg" alt="RoundOne Logo" className="h-8" />
        </div>
      </header>
      
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="container max-w-md mx-auto">
          <Card className="border border-[#9b87f5]/20 shadow-md overflow-hidden">
            <CardContent className="p-8">
              {isVerifying ? (
                <div className="text-center py-8">
                  <Loader2 className="h-12 w-12 text-[#9b87f5] animate-spin mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-[#1A1F2C] mb-2">Verifying Your Email</h1>
                  <p className="text-gray-500">Please wait while we verify your company email address...</p>
                </div>
              ) : isSuccess ? (
                <div className="text-center py-4">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-[#1A1F2C] mb-2">Email Verified!</h1>
                  <p className="text-gray-500 mb-6">
                    Your company email has been successfully verified. You can now use all recruiter features of the platform.
                  </p>
                  <Button 
                    className="w-full bg-[#9b87f5] text-white hover:bg-[#8976e9]"
                    onClick={handleContinue}
                  >
                    Continue to Dashboard
                  </Button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-[#1A1F2C] mb-2">Verification Failed</h1>
                  <p className="text-gray-500 mb-6">
                    {errorMessage || 'We could not verify your email address. The link may be invalid or expired.'}
                  </p>
                  <Button 
                    className="w-full bg-[#9b87f5] text-white hover:bg-[#8976e9]"
                    onClick={handleSignIn}
                  >
                    Go to Sign In
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default EmailVerification; 